import { isValidElement, ComponentType, ReactNode } from 'react'
import { WizardSchema, WizardStateSchema } from '@/lib/definitions/wizard.types'
import { ProductIdentifiers } from '@/lib/definitions/types'
import Button from '@/components/Button'
import { m } from 'framer-motion'
import BookOpenIcon from '@icons/book-open'
import { cn } from '@/lib/misc/Utils'
import CodeIcon from '@icons/code-bracket'
import ArrowTopRightOnSquareIcon from '@icons/arrow-top-right-on-square'
import { useKnowledgeBaseLink } from '@/lib/hooks/useKnowledgeBaseLink'

interface WizardHeaderProps<T extends WizardStateSchema = WizardStateSchema> {
  schema: WizardSchema<T>
  productId?: ProductIdentifiers
  className?: string
}

function renderIcon(
  icon:
    | ReactNode
    | ComponentType<{ productId: ProductIdentifiers }>
    | undefined,
  productId?: ProductIdentifiers
): ReactNode {
  if (!icon) return null
  if (isValidElement(icon)) return icon
  if (typeof icon === 'function') {
    const Icon = icon as ComponentType<{ productId: ProductIdentifiers }>
    return (
      <Icon productId={productId || ('ip_transit' as ProductIdentifiers)} />
    )
  }
  return icon as ReactNode
}

const WizardHeader = <T extends WizardStateSchema = WizardStateSchema>({
  schema,
  productId,
  className,
}: WizardHeaderProps<T>) => {
  const knowledgeBaseLink = useKnowledgeBaseLink()
  return (
    <m.div
      className={cn('', className)}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="flex items-center gap-4">
        <m.div
          className="size-28 shrink-0 rounded-2xl bg-surface border border-hairline shadow-card flex items-center justify-center text-neutral-70"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
        >
          <span className="[&>svg]:size-12">
            {renderIcon(schema.icon, productId)}
          </span>
        </m.div>
        <m.div
          className="flex-1 flex flex-col"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: 0.2 }}
        >
          <h1 className="text-3xl font-semibold text-neutral-90 mb-1">
            {schema.title}
          </h1>
          {schema.description && (
            <p className="text-neutral-50">{schema.description}</p>
          )}
          {(schema.docsUrl || schema.apiUrl) && (
            <div className="flex gap-x-2 mt-4">
              {schema.docsUrl && (
                <Button
                  as="anchor"
                  {...knowledgeBaseLink(schema.docsUrl)}
                  style="secondary"
                  size="sm"
                  icon={<BookOpenIcon className="size-4" />}
                  iconSuffix={
                    <ArrowTopRightOnSquareIcon className="opacity-50" />
                  }
                >
                  Knowledge Base
                </Button>
              )}
              {schema.apiUrl && (
                <Button
                  as="anchor"
                  href={schema.apiUrl}
                  target="_blank"
                  style="secondary"
                  size="sm"
                  icon={<CodeIcon className="size-4" />}
                  iconSuffix={
                    <ArrowTopRightOnSquareIcon className="opacity-50" />
                  }
                >
                  API Reference
                </Button>
              )}
            </div>
          )}
        </m.div>
      </div>
    </m.div>
  )
}

// Keep the old name as an export for backward compatibility
export const IpTransitV2Header = WizardHeader

export default WizardHeader
